import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useFamily } from '../../contexts/FamilyContext'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import toast from 'react-hot-toast'

const { FiX, FiPlus, FiUser, FiUsers, FiCalendar, FiClock, FiRepeat } = FiIcons

const CreateTaskModal = ({ isOpen, onClose }) => {
  const { createTask } = useFamily()
  const { family } = useAuth()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    points: 10,
    category: 'other',
    assigned_to: '', // Can be 'all' for all children or specific child ID
    recurring_pattern: 'once',
    recurring_days: [], // For weekly tasks
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    due_date: '',
    estimated_time_minutes: 15 // New field for estimated time
  })

  const categories = [
    { value: 'cleaning', label: 'Cleaning', emoji: '🧹', defaultTime: 15 },
    { value: 'dishes', label: 'Dishes', emoji: '🍽️', defaultTime: 10 },
    { value: 'laundry', label: 'Laundry', emoji: '👕', defaultTime: 5 },
    { value: 'pets', label: 'Pet Care', emoji: '🐕', defaultTime: 10 },
    { value: 'homework', label: 'Homework', emoji: '📚', defaultTime: 30 },
    { value: 'outdoor', label: 'Outdoor', emoji: '🌱', defaultTime: 20 },
    { value: 'cooking', label: 'Cooking', emoji: '👨‍🍳', defaultTime: 25 },
    { value: 'organization', label: 'Organization', emoji: '📦', defaultTime: 15 },
    { value: 'other', label: 'Other', emoji: '⭐', defaultTime: 10 }
  ]

  const timePresets = [
    { label: '5 min', value: 5, category: 'quick' },
    { label: '10 min', value: 10, category: 'quick' },
    { label: '15 min', value: 15, category: 'medium' },
    { label: '20 min', value: 20, category: 'medium' },
    { label: '30 min', value: 30, category: 'medium' },
    { label: '45 min', value: 45, category: 'long' },
    { label: '1 hour', value: 60, category: 'long' },
    { label: '2 hours', value: 120, category: 'long' }
  ]

  const recurringOptions = [
    { value: 'once', label: 'One Time Only', icon: '📅', description: 'Complete this task once' },
    { value: 'daily', label: 'Every Day', icon: '🔄', description: 'Repeat daily' },
    { value: 'weekly', label: 'Weekly', icon: '📆', description: 'Repeat on specific days of the week' },
    { value: 'monthly', label: 'Monthly', icon: '🗓️', description: 'Repeat monthly' }
  ]

  const weekDays = [
    { value: 0, label: 'Sunday', short: 'Sun' },
    { value: 1, label: 'Monday', short: 'Mon' },
    { value: 2, label: 'Tuesday', short: 'Tue' },
    { value: 3, label: 'Wednesday', short: 'Wed' },
    { value: 4, label: 'Thursday', short: 'Thu' },
    { value: 5, label: 'Friday', short: 'Fri' },
    { value: 6, label: 'Saturday', short: 'Sat' }
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!family) {
      toast.error('No family found. Please refresh and try again.')
      return
    }

    setLoading(true)
    try {
      console.log('Creating task with data:', formData)

      // Create base task data
      const baseTaskData = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        points: parseInt(formData.points),
        category: formData.category,
        recurring_pattern: formData.recurring_pattern,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        due_date: formData.due_date || null,
        estimated_time_minutes: parseInt(formData.estimated_time_minutes) || null,
        family_id: family.id
      }

      // Handle weekly recurring days
      if (formData.recurring_pattern === 'weekly' && formData.recurring_days.length > 0) {
        baseTaskData.recurring_days = formData.recurring_days
      }

      // Handle task assignment
      if (formData.assigned_to === 'all' || formData.assigned_to === '') {
        // Create task for all children or unassigned
        baseTaskData.assigned_to = null
        const { data, error } = await createTask(baseTaskData)
        
        if (error) {
          console.error('Task creation error:', error)
          toast.error(`Failed to create task: ${error.message || 'Unknown error'}`)
        } else {
          console.log('Task created successfully:', data)
          toast.success('Task created successfully! 🎉')
        }
      } else {
        // Create task for specific child
        baseTaskData.assigned_to = formData.assigned_to
        const { data, error } = await createTask(baseTaskData)
        
        if (error) {
          console.error('Task creation error:', error)
          toast.error(`Failed to create task: ${error.message || 'Unknown error'}`)
        } else {
          console.log('Task created successfully:', data)
          const childName = family.children?.find(c => c.id === formData.assigned_to)?.name || 'child'
          toast.success(`Task assigned to ${childName}! 🎉`)
        }
      }

      // Reset form and close modal
      onClose()
      setFormData({
        title: '',
        description: '',
        points: 10,
        category: 'other',
        assigned_to: '',
        recurring_pattern: 'once',
        recurring_days: [],
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        due_date: '',
        estimated_time_minutes: 15
      })

    } catch (error) {
      console.error('Unexpected error creating task:', error)
      toast.error('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleCategoryChange = (category) => {
    const categoryData = categories.find(c => c.value === category)
    setFormData(prev => ({
      ...prev,
      category,
      estimated_time_minutes: categoryData?.defaultTime || 15
    }))
  }

  const handleRecurringDayToggle = (dayValue) => {
    setFormData(prev => ({
      ...prev,
      recurring_days: prev.recurring_days.includes(dayValue)
        ? prev.recurring_days.filter(d => d !== dayValue)
        : [...prev.recurring_days, dayValue].sort()
    }))
  }

  const getAssignmentText = () => {
    if (!formData.assigned_to || formData.assigned_to === 'all') {
      return 'Available to all children'
    }
    const child = family?.children?.find(c => c.id === formData.assigned_to)
    return child ? `Assigned to ${child.name}` : 'Select child'
  }

  const getRecurringPreview = () => {
    switch (formData.recurring_pattern) {
      case 'once':
        return 'One-time task'
      case 'daily':
        return 'Repeats every day'
      case 'weekly':
        if (formData.recurring_days.length === 0) return 'Select days of the week'
        const dayNames = formData.recurring_days
          .map(d => weekDays.find(wd => wd.value === d)?.short)
          .join(', ')
        return `Repeats every ${dayNames}`
      case 'monthly':
        return 'Repeats monthly'
      default:
        return ''
    }
  }

  const formatTimeDisplay = (minutes) => {
    if (!minutes) return ''
    if (minutes < 60) {
      return `${minutes} min`
    }
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }

  const getTimeBadgeColor = (minutes) => {
    if (minutes <= 10) return 'success'
    if (minutes <= 30) return 'warning'
    return 'secondary'
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Create New Task</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Task Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Task Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., Make your bed"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description (Optional)
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Add any specific instructions or details..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Points Reward
                    </label>
                    <input
                      type="number"
                      name="points"
                      value={formData.points}
                      onChange={handleChange}
                      min="1"
                      max="100"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Estimated Time Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    <SafeIcon icon={FiClock} className="inline mr-2" />
                    Estimated Time
                  </label>
                  
                  {/* Time Presets */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {timePresets.map((preset) => (
                      <button
                        key={preset.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, estimated_time_minutes: preset.value }))}
                        className={`
                          p-2 rounded-lg border-2 transition-all duration-200 text-center text-sm
                          ${formData.estimated_time_minutes === preset.value
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>

                  {/* Custom Time Input */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <input
                        type="number"
                        name="estimated_time_minutes"
                        value={formData.estimated_time_minutes}
                        onChange={handleChange}
                        min="1"
                        max="480"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Minutes"
                      />
                    </div>
                    <div className="text-sm text-gray-500">
                      minutes
                    </div>
                    {formData.estimated_time_minutes && (
                      <Badge variant={getTimeBadgeColor(formData.estimated_time_minutes)}>
                        {formatTimeDisplay(formData.estimated_time_minutes)}
                      </Badge>
                    )}
                  </div>

                  <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                    <div className="text-sm text-blue-700">
                      💡 This helps kids plan their time and parents estimate completion
                    </div>
                  </div>
                </div>

                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Category
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {categories.map((category) => (
                      <button
                        key={category.value}
                        type="button"
                        onClick={() => handleCategoryChange(category.value)}
                        className={`
                          p-3 rounded-lg border-2 transition-all duration-200 text-center
                          ${formData.category === category.value
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                      >
                        <div className="text-2xl mb-1">{category.emoji}</div>
                        <div className="text-xs font-medium">{category.label}</div>
                        <div className="text-xs text-gray-500">{category.defaultTime}m</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Assignment Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    <SafeIcon icon={FiUser} className="inline mr-2" />
                    Assign Task
                  </label>
                  <div className="space-y-3">
                    {/* All Children Option */}
                    <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="assigned_to"
                        value="all"
                        checked={formData.assigned_to === 'all' || formData.assigned_to === ''}
                        onChange={handleChange}
                        className="mr-3"
                      />
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={FiUsers} className="text-primary-500" />
                        <div>
                          <div className="font-medium">All Children</div>
                          <div className="text-sm text-gray-500">Anyone can complete this task</div>
                        </div>
                      </div>
                    </label>

                    {/* Individual Children */}
                    {family?.children && family.children.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-sm text-gray-600 font-medium">Or assign to specific child:</div>
                        {family.children.map((child) => (
                          <label
                            key={child.id}
                            className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                          >
                            <input
                              type="radio"
                              name="assigned_to"
                              value={child.id}
                              checked={formData.assigned_to === child.id}
                              onChange={handleChange}
                              className="mr-3"
                            />
                            <div className="flex items-center gap-3">
                              <div className="text-2xl">{child.avatar || '👦'}</div>
                              <div>
                                <div className="font-medium">{child.name}</div>
                                {child.age && (
                                  <div className="text-sm text-gray-500">Age {child.age}</div>
                                )}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                    <div className="text-sm text-blue-700 font-medium">
                      📋 {getAssignmentText()}
                    </div>
                  </div>
                </div>

                {/* Recurring Pattern */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    <SafeIcon icon={FiRepeat} className="inline mr-2" />
                    Recurring Schedule
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {recurringOptions.map((option) => (
                      <label
                        key={option.value}
                        className={`
                          flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200
                          ${formData.recurring_pattern === option.value
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                      >
                        <input
                          type="radio"
                          name="recurring_pattern"
                          value={option.value}
                          checked={formData.recurring_pattern === option.value}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">{option.icon}</div>
                          <div>
                            <div className="font-medium">{option.label}</div>
                            <div className="text-sm text-gray-500">{option.description}</div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Weekly Days Selection */}
                  {formData.recurring_pattern === 'weekly' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-4 p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="text-sm font-medium text-gray-700 mb-3">
                        Select days of the week:
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {weekDays.map((day) => (
                          <button
                            key={day.value}
                            type="button"
                            onClick={() => handleRecurringDayToggle(day.value)}
                            className={`
                              px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                              ${formData.recurring_days.includes(day.value)
                                ? 'bg-primary-500 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }
                            `}
                          >
                            {day.short}
                          </button>
                        ))}
                      </div>
                      {formData.recurring_days.length === 0 && (
                        <div className="text-sm text-red-500 mt-2">
                          Please select at least one day for weekly tasks
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* End Date for Recurring Tasks */}
                  {formData.recurring_pattern !== 'once' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-4"
                    >
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date (Optional)
                      </label>
                      <input
                        type="date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleChange}
                        min={formData.start_date}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        Leave empty for tasks that continue indefinitely
                      </div>
                    </motion.div>
                  )}

                  {/* Schedule Preview */}
                  <div className="mt-3 p-3 bg-green-50 rounded-lg">
                    <div className="text-sm text-green-700 font-medium">
                      📅 {getRecurringPreview()}
                    </div>
                  </div>
                </div>

                {/* Due Date for One-time Tasks */}
                {formData.recurring_pattern === 'once' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Due Date (Optional)
                    </label>
                    <input
                      type="date"
                      name="due_date"
                      value={formData.due_date}
                      onChange={handleChange}
                      min={formData.start_date}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    className="flex-1"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={loading}
                    className="flex-1"
                    disabled={formData.recurring_pattern === 'weekly' && formData.recurring_days.length === 0}
                  >
                    <SafeIcon icon={FiPlus} className="text-sm" />
                    Create Task
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default CreateTaskModal